/**
 * Related Documents Component
 * Shows document relationship chain (Quotation → SO → WO → Job Card → Inspection → Invoice)
 * Provides deep linking between related documents
 */

import React from 'react';
import { Card, Timeline, Tag, Button, Space, Typography, Empty } from 'antd';
import {
  FileTextOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './RelatedDocuments.css';

const { Text, Link } = Typography;

interface RelatedDocument {
  id: string;
  type: 'quotation' | 'sales-order' | 'work-order' | 'job-card' | 'inspection' | 'invoice';
  number: string;
  date: string;
  status: string;
  route: string;
}

interface RelatedDocumentsProps {
  documents: RelatedDocument[];
  currentDocumentId?: string;
}

const documentTypeConfig = {
  quotation: {
    label: 'Quotation',
    icon: <FileTextOutlined />,
    color: '#145A54',
  },
  'sales-order': {
    label: 'Customer Order',
    icon: <ShoppingCartOutlined />,
    color: '#52c41a',
  },
  'work-order': {
    label: 'Work Order',
    icon: <ToolOutlined />,
    color: '#722ed1',
  },
  'job-card': {
    label: 'Job Card',
    icon: <FileTextOutlined />,
    color: '#fa8c16',
  },
  inspection: {
    label: 'Quality Inspection',
    icon: <SafetyCertificateOutlined />,
    color: '#13c2c2',
  },
  invoice: {
    label: 'Invoice',
    icon: <DollarOutlined />,
    color: '#eb2f96',
  },
};

const getStatusColor = (status: string): string => {
  const statusMap: Record<string, string> = {
    'Draft': 'default',
    'Submitted': 'processing',
    'Approved': 'success',
    'In Progress': 'processing',
    'Completed': 'success',
    'Closed': 'default',
    'Rejected': 'error',
    'Pending': 'warning',
  };
  return statusMap[status] || 'default';
};

const getStatusIcon = (status: string) => {
  if (status === 'Completed' || status === 'Approved') {
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  }
  if (status === 'In Progress' || status === 'Submitted') {
    return <ClockCircleOutlined style={{ color: '#145A54' }} />;
  }
  return null;
};

export const RelatedDocuments: React.FC<RelatedDocumentsProps> = ({
  documents,
  currentDocumentId,
}) => {
  const navigate = useNavigate();

  if (!documents || documents.length === 0) {
    return (
      <Card title="📋 Related Documents" size="small">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No related documents"
        />
      </Card>
    );
  }

  return (
    <Card 
      className="related-documents-card"
      title={
        <Space>
          <Text strong>📋 Document Chain</Text>
          <Tag color="blue">{documents.length} documents</Tag>
        </Space>
      }
      size="small"
    >
      <Timeline>
        {documents.map((doc, index) => {
          const config = documentTypeConfig[doc.type];
          const isCurrent = doc.id === currentDocumentId;
          
          return (
            <Timeline.Item
              key={doc.id}
              dot={config.icon}
              color={isCurrent ? config.color : 'gray'}
            >
              <div className="timeline-item-content">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    {isCurrent && (
                      <Tag color="blue">Current</Tag>
                    )}
                    <Text strong style={{ color: config.color }}>
                      {config.label}
                    </Text>
                  </Space>
                  
                  <Space>
                    <Link onClick={() => navigate(doc.route)}>
                      {doc.number}
                    </Link>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {doc.date}
                    </Text>
                  </Space>

                  <Space>
                    <Tag color={getStatusColor(doc.status)} icon={getStatusIcon(doc.status)}>
                      {doc.status}
                    </Tag>
                  </Space>

                  {!isCurrent && (
                    <Button
                      type="link"
                      size="small"
                      icon={<ArrowRightOutlined />}
                      onClick={() => navigate(doc.route)}
                      style={{ padding: 0 }}
                    >
                      View Document
                    </Button>
                  )}
                </Space>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>

      <div style={{ marginTop: 16, padding: 12, background: '#f0f5ff', borderRadius: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          💡 Click on any document number to view its details
        </Text>
      </div>
    </Card>
  );
};

export default RelatedDocuments;
